import {
  MOVE_TO_SET_TYPES,
  type MoveToSetTarget,
  type MoveToSetType,
} from "../../../../api/pipelineBatchUpdates.service.js";
import { UUID_PATTERN } from "./csvHelpers.js";
import { fieldCountProblem, readCsvRecords, readText } from "./parseCsvRecords.js";

export const SIMPLE_HEADER = ["testimonial_id", "testimonial_type"] as const;

export const WORKLIST_HEADER = [
  "pipeline_id",
  "approval_id",
  "status_today",
  "edition",
  "set",
  "office",
  "practice_area",
  "filed_as",
  "barrister",
  "contact_id",
  "firm_ref",
  "company_id",
  "praise_for",
  "move_change",
  "ai_confidence",
  "testimonial",
  "text_to_publish",
] as const;

const WORKLIST_REQUIRED = ["pipeline_id", "move_change", "filed_as", "company_id"] as const;

const SET_TYPE_PATTERN = /^(?:clear contact_id;\s*)?set testimonial_type to ([^\s.;,]+)/;

const CLEAR_CONTACT_ONLY = "clear contact_id";

const NO_CHANGE_PREFIX = "none:";

export type MoveToSetCsvFormat = "simple" | "worklist";

export type MoveToSetSkipReason = "no_change_needed" | "no_company_id";

export const SKIP_REASON_LABELS: Record<MoveToSetSkipReason, string> = {
  no_change_needed: "no change needed",
  no_company_id: "set has no company_id, would show on no page",
};

export type MoveToSetCsvRow = MoveToSetTarget & { line: number };

export type SkippedMoveToSetRow = {
  line: number;
  testimonialId: string;
  reason: MoveToSetSkipReason;
};

export type InvalidMoveToSetRow = {
  line: number;
  testimonialId: string;
  reason: string;
};

export type ParsedMoveToSetCsv = {
  format: MoveToSetCsvFormat;
  rows: MoveToSetCsvRow[];
  noCompanyIdRows: MoveToSetCsvRow[];
  skipped: SkippedMoveToSetRow[];
  invalid: InvalidMoveToSetRow[];
  duplicates: string[];
};

export type MoveToSetSelection = {
  rows: MoveToSetCsvRow[];
  skipped: SkippedMoveToSetRow[];
};

type Classified =
  | { kind: "row"; row: MoveToSetCsvRow }
  | { kind: "noCompanyId"; row: MoveToSetCsvRow }
  | { kind: "skipped"; skipped: SkippedMoveToSetRow }
  | { kind: "invalid"; invalid: InvalidMoveToSetRow };

type Field = (name: string) => string;

const TYPE_LIST = MOVE_TO_SET_TYPES.join(" or ");

function isMoveToSetType(value: string): value is MoveToSetType {
  return (MOVE_TO_SET_TYPES as readonly string[]).includes(value);
}

function byLine<T extends { line: number }>(a: T, b: T): number {
  return a.line - b.line;
}

function detectFormat(header: string[]): MoveToSetCsvFormat {
  const present = new Set(header);
  if (SIMPLE_HEADER.every(column => present.has(column))) return "simple";
  if (WORKLIST_REQUIRED.every(column => present.has(column))) return "worklist";
  throw new Error(
    `CSV header not recognised. Expected either ${SIMPLE_HEADER.join(",")} or the worklist export header ${WORKLIST_HEADER.join(",")}`,
  );
}

function classifySimple(field: Field, line: number): Classified {
  const testimonialId = field("testimonial_id").toLowerCase();
  const testimonialType = field("testimonial_type").toLowerCase();
  if (!UUID_PATTERN.test(testimonialId)) {
    return {
      kind: "invalid",
      invalid: { line, testimonialId, reason: "testimonial_id is not a UUID" },
    };
  }
  if (!isMoveToSetType(testimonialType)) {
    return {
      kind: "invalid",
      invalid: {
        line,
        testimonialId,
        reason: `testimonial_type "${testimonialType}" must be ${TYPE_LIST}`,
      },
    };
  }
  return { kind: "row", row: { line, testimonialId, testimonialType } };
}

function worklistTargetType(
  moveChange: string,
  filedAs: string,
): { type: MoveToSetType } | { reason: string } {
  const match = SET_TYPE_PATTERN.exec(moveChange);
  if (match) {
    const derived = match[1];
    return isMoveToSetType(derived)
      ? { type: derived }
      : { reason: `move_change sets testimonial_type to "${derived}", expected ${TYPE_LIST}` };
  }
  if (moveChange === CLEAR_CONTACT_ONLY) {
    const filed = filedAs.toLowerCase();
    return isMoveToSetType(filed)
      ? { type: filed }
      : {
          reason: `move_change is "clear contact_id" but filed_as is "${filed}", expected ${TYPE_LIST}`,
        };
  }
  return { reason: `unknown move_change "${moveChange}"` };
}

function classifyWorklist(field: Field, line: number): Classified {
  const testimonialId = field("pipeline_id").toLowerCase();
  if (!UUID_PATTERN.test(testimonialId)) {
    return {
      kind: "invalid",
      invalid: { line, testimonialId, reason: "pipeline_id is not a UUID" },
    };
  }
  const moveChange = field("move_change");
  if (moveChange.startsWith(NO_CHANGE_PREFIX)) {
    return { kind: "skipped", skipped: { line, testimonialId, reason: "no_change_needed" } };
  }
  const target = worklistTargetType(moveChange, field("filed_as"));
  if ("reason" in target) {
    return { kind: "invalid", invalid: { line, testimonialId, reason: target.reason } };
  }
  const row = { line, testimonialId, testimonialType: target.type };
  return field("company_id") === "" ? { kind: "noCompanyId", row } : { kind: "row", row };
}

function findDuplicates(ids: string[]): string[] {
  const occurrences = new Map<string, number>();
  for (const id of ids) occurrences.set(id, (occurrences.get(id) ?? 0) + 1);
  return [...occurrences.entries()].filter(([, total]) => total > 1).map(([id]) => id);
}

export async function parseMoveToSetCsv(input: File | string): Promise<ParsedMoveToSetCsv> {
  const { header, records } = readCsvRecords(await readText(input));
  const format = detectFormat(header);
  const classify = format === "simple" ? classifySimple : classifyWorklist;
  const columnIndex = new Map(header.map((column, index) => [column, index]));

  const rows: MoveToSetCsvRow[] = [];
  const noCompanyIdRows: MoveToSetCsvRow[] = [];
  const skipped: SkippedMoveToSetRow[] = [];
  const invalid: InvalidMoveToSetRow[] = [];

  for (const { line, fields } of records) {
    if (fields.every(value => value.trim() === "")) continue;
    const field: Field = name => (fields[columnIndex.get(name) ?? -1] ?? "").trim();
    const countProblem = fieldCountProblem(header, fields);
    const classified: Classified = countProblem
      ? {
          kind: "invalid",
          invalid: {
            line,
            testimonialId: field(format === "simple" ? "testimonial_id" : "pipeline_id"),
            reason: countProblem,
          },
        }
      : classify(field, line);
    if (classified.kind === "row") rows.push(classified.row);
    else if (classified.kind === "noCompanyId") noCompanyIdRows.push(classified.row);
    else if (classified.kind === "skipped") skipped.push(classified.skipped);
    else invalid.push(classified.invalid);
  }

  const duplicates = findDuplicates(
    [...rows, ...noCompanyIdRows, ...skipped, ...invalid]
      .map(entry => entry.testimonialId)
      .filter(id => UUID_PATTERN.test(id)),
  );
  const duplicateSet = new Set(duplicates);
  const unique = (row: MoveToSetCsvRow) => !duplicateSet.has(row.testimonialId);

  return {
    format,
    rows: rows.filter(unique),
    noCompanyIdRows: noCompanyIdRows.filter(unique),
    skipped,
    invalid,
    duplicates,
  };
}

export function selectMoveToSetRows(
  parsed: ParsedMoveToSetCsv,
  includeNoCompanyId: boolean,
): MoveToSetSelection {
  if (includeNoCompanyId) {
    return {
      rows: [...parsed.rows, ...parsed.noCompanyIdRows].sort(byLine),
      skipped: parsed.skipped,
    };
  }
  const excluded = parsed.noCompanyIdRows.map(
    ({ line, testimonialId }): SkippedMoveToSetRow => ({
      line,
      testimonialId,
      reason: "no_company_id",
    }),
  );
  return { rows: parsed.rows, skipped: [...parsed.skipped, ...excluded].sort(byLine) };
}

export function countSkippedByReason(
  skipped: SkippedMoveToSetRow[],
): Partial<Record<MoveToSetSkipReason, number>> {
  const counts: Partial<Record<MoveToSetSkipReason, number>> = {};
  for (const { reason } of skipped) counts[reason] = (counts[reason] ?? 0) + 1;
  return counts;
}
